import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { LocalLocationService } from '../../shared/local-location.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { Trailer } from '../../shared/trailer.model';
import { FeatureCompatibility } from '../../../shared/feature-compatibility';
import { ModalService } from '../../../shared/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalLocationContainerConfiguration } from '../../shared/local-location-container-configuration.model';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { LocalLocationContainer } from '../../shared/local-location-container.model';
import { Latng } from '../../../shared/latng';
import { ToastrService } from 'ngx-toastr';
import { LocalLocationOptionConfiguration, PrintMode } from '../../shared/local-location-option-configuration.model';
import { LocalLocationProfileConfiguration } from '../../shared/local-location-profile-configuration.model';
import { Region } from '../../../user-dashboard/shared/region.model';
import { BufferDurations } from '../../../user-dashboard/shared/buffer-durations.model';
import { RegionService } from '../../../user-dashboard/shared/region.service';
import { EtaApproximationSource } from '../../../shared/eta-approximation-source';
import * as moment from 'moment-timezone';
import { LocalLocationType } from '../../shared/local-location-type';
import { DistanceUnit } from 'app/shared/distance-unit';
import { ValueWithTranslatedLabel } from '../../shared/value-with-translated-label.model';
import { LanguageService } from '../../../shared/services/language.service';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { NavigationLinkService } from '../../../shared/services/navigation-link.service';
import { CmaLiteCarriersConfiguration } from '../../../shared/cma-lite-carriers-configuration';
import { CmaLiteDeliveriesConfiguration } from '../../../shared/cma-lite-deliveries-configuration';
import { CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { NgForm, NgModel } from '@angular/forms';
import { RegionFeatureStatus } from '../../../user-dashboard/shared/region-feature-status.model';
import { FormsUtils } from '../../../shared/forms-utils';
import { OnOffFeature } from '../../../user-dashboard/shared/on-off-feature.model';

@Component({
  selector: 'app-location-detail',
  templateUrl: './location-detail.component.html',
  styleUrls: ['./location-detail.component.scss']
})
export class LocationDetailComponent implements OnInit, OnChanges {

  protected readonly OnOffFeature = OnOffFeature;

  @Input()
  location: LocalLocationContainer;

  @Input()
  canManageLocations: boolean;

  @Output()
  onClose = new EventEmitter<void>();

  activeTab = 'info';

  locationCarrierConfigEditOpen: boolean;

  CmaLiteCarriersConfiguration = CmaLiteCarriersConfiguration;

  configurationForm: { region: Region, bufferDurations: BufferDurations, enableForceCheckin: boolean, cicoMobile: boolean } = {
    region: new Region(),
    bufferDurations: new BufferDurations(),
    enableForceCheckin: true,
    cicoMobile: true
  };

  optionForm: LocalLocationOptionConfiguration = new LocalLocationOptionConfiguration();

  profileForm: LocalLocationProfileConfiguration = new LocalLocationProfileConfiguration();

  trailers: Trailer[] = [];

  options: LocalLocationOptionConfiguration[] = [];

  profiles: LocalLocationProfileConfiguration[] = [];

  FeatureCompatibility = FeatureCompatibility;

  EtaApproximationSource = EtaApproximationSource;

  LocationType = LocalLocationType;

  printModes = Object.keys(PrintMode);

  onConfig = false;

  isActionDropdownOpen = false;

  etaApproximationSource = EtaApproximationSource.REGION;

  currentBufferDurations = new BufferDurations();

  allRegions: Region[] = [];

  sapTimezone: string;

  googleTimezone: string;

  CONVERSION_CONSTANT = 1609.34;
  FORCED_CHECKIN_MAX_RADIUS_MIN_VALUE = 100;
  FORCED_CHECKIN_MAX_RADIUS_MAX_VALUE = 500;

  forcedCheckinMaxRadiusInMiles = 0.3;
  forcedCheckinMaxRadiusInMeters = 500;

  displayedHelp = false;

  trailerStatuses: ValueWithTranslatedLabel[] = [];

  canManageRegions = false;
  cmaLiteActivated: boolean;
  cmaLiteCarriersConfiguration: CmaLiteCarriersConfiguration;
  cmaLiteDeliveriesConfiguration: CmaLiteDeliveriesConfiguration;
  cmaLiteCarrierLinkedNumber = 0;
  CmaLiteDeliveriesConfiguration = CmaLiteDeliveriesConfiguration;
  CmaLiteCarrierConfiguration = CmaLiteCarriersConfiguration;

  cmaCicoAlignment: boolean;
  askZ2EtaCico: boolean;
  z1CheckoutToMyChep: boolean;
  documentDownloadQrCode: OnOffFeature;


  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountry: CountryISO;

  @ViewChild('phoneInput')
  phoneInput: NgModel;

  @ViewChild('locationDetailForm')
  locationDetailForm!: NgForm;

  DistanceUnit = DistanceUnit;

  protected readonly RegionFeatureStatus = RegionFeatureStatus;

  constructor(
    private locationService: LocalLocationService,
    private loader: LoaderService,
    private translate: TranslateService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
    private regionService: RegionService,
    private localLocationService: LocalLocationService,
    private languageService: LanguageService,
    public link: NavigationLinkService,
    public authService: AuthenticationService
  ) {
    this.canManageRegions = this.authService.canManageRegions();
  }

  ngOnInit() {
    if (this.authService.canManageRegions() || this.authService.canReadRegions()) {
      this.regionService.getAll().subscribe(regions => {
        this.allRegions = regions;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.location && changes.location.currentValue) {
      if (this.authService.canViewTrailerReports() || this.authService.canManageLocations()) {
        this.fetchLocationTrailers();
      }
      if (this.authService.canManageLocationsOrPlants()) {
        this.fetchTimezones();

        if (this.location.type === LocalLocationType.CHEP) {
          this.preferredCountry = this.location?.address?.country?.toLowerCase();
          this.fetchLocationDetails();
          this.fetchLocationOptions();
          this.fetchLocationProfiles();
        }
      }
    }
  }

  fetchLocationDetails() {
    this.loader.startLoad();

    this.locationService.getLocation(this.location.id)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(location => {
        this.location = location;
      })
  }

  fetchLocationTrailers() {
    this.loader.startLoad();

    this.languageService.currentLanguage.subscribe(l => {
      this.locationService.getLocationTrailerStatus(this.location.glid, l)
        .subscribe(statuses => {
          this.trailerStatuses = statuses;
          this.locationService.getLocationTrailers(this.location.id)
            .pipe(finalize(() => this.loader.stopLoad()))
            .subscribe(trailers => {
              this.trailers = trailers;
            });
        });
    });
  }

  fetchLocationOptions() {
    this.loader.startLoad();

    this.locationService.getLocationOptions(this.location.id)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(options => {
        this.options = options;
      })
  }

  fetchLocationProfiles() {
    this.loader.startLoad();

    this.locationService.getLocationProfiles(this.location.id)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(profiles => {
        this.profiles = profiles;
      })
  }

  fetchTimezones() {
    if (this.location.configuration.sapTimezone) {
      const offset = moment().tz(this.location.configuration.sapTimezone).format('Z');
      this.sapTimezone = this.location.configuration.sapTimezone + ' (UTC' + offset + ')';
    } else {
      this.sapTimezone = null;
    }

    if (this.location.configuration.googleTimezone) {
      const offset = moment().tz(this.location.configuration.googleTimezone).format('Z');
      this.googleTimezone = this.location.configuration.googleTimezone + ' (UTC' + offset + ')';
    } else {
      this.googleTimezone = null;
    }
  }

  exportSapConfig() {
    this.loader.startLoad()

    this.locationService.exportSapConfiguration(this.location.glid)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe();
  }

  openConfigureLocation() {
    this.configurationForm = {
      region: this.location.configuration.region,
      bufferDurations: null,
      enableForceCheckin: this.location.configuration.enableForceCheckin,
      cicoMobile: this.location.configuration.cicoMobileEnabled
    };

    this.forcedCheckinMaxRadiusInMeters = this.location.configuration.forcedCheckinMaxRadius;
    this.forcedCheckinMaxRadiusInMiles = this.getConvertedMilesRadius(this.forcedCheckinMaxRadiusInMeters);

    this.cmaLiteActivated = this.location.configuration.cmaLiteActivated;
    this.cmaLiteDeliveriesConfiguration = this.location.configuration.cmaLiteDeliveriesConfiguration;
    this.cmaLiteCarriersConfiguration = this.location.configuration.cmaLiteCarriersConfiguration;

    this.askZ2EtaCico = this.location.configuration.askZ2EtaCico && this.location.configuration.region.features.askZ2EtaCico !== RegionFeatureStatus.NOT_AVAILABLE;

    this.z1CheckoutToMyChep = this.location.configuration.z1CheckoutToMyChep && this.location.configuration.region.features.z1CheckoutToMyChep !== RegionFeatureStatus.NOT_AVAILABLE;

    this.cmaCicoAlignment = this.location.configuration.cmaCicoAlignment && this.location.configuration.region.features.cmaCicoAlignment !== RegionFeatureStatus.NOT_AVAILABLE;

    this.fetchCarrierLinkedCount();

    if (this.location.configuration.overriddenBufferDuration != null) {
      this.etaApproximationSource = EtaApproximationSource.PERSONALIZED;
      this.configurationForm.bufferDurations = this.location.configuration.overriddenBufferDuration;
      this.currentBufferDurations = this.location.configuration.overriddenBufferDuration;
    } else {
      this.etaApproximationSource = EtaApproximationSource.REGION;
      this.configurationForm.bufferDurations =  this.configurationForm.region.bufferDurations;
    }
  }

  updateLocationConfiguration() {
    this.loader.startLoad();

    const configuration = new LocalLocationContainerConfiguration(this.configurationForm.region);
    configuration.ymsCompatible = this.location.configuration.ymsCompatible;

    if (this.etaApproximationSource === EtaApproximationSource.PERSONALIZED) {
      configuration.overriddenBufferDuration = this.configurationForm.bufferDurations;
    }
    configuration.enableForceCheckin = this.configurationForm.enableForceCheckin;
    configuration.cicoMobileEnabled = this.configurationForm.cicoMobile;

    // cma lite
    configuration.cmaLiteCarriersConfiguration = this.cmaLiteCarriersConfiguration;
    configuration.cmaLiteDeliveriesConfiguration = this.cmaLiteDeliveriesConfiguration;
    configuration.cmaLiteActivated = this.cmaLiteActivated;

    configuration.askZ2EtaCico = this.askZ2EtaCico;
    configuration.z1CheckoutToMyChep = this.z1CheckoutToMyChep;
    configuration.documentDownloadQrCodeFeature = this.documentDownloadQrCode;
    configuration.cmaCicoAlignment = this.cmaCicoAlignment;

    configuration.forcedCheckinMaxRadius = this.forcedCheckinMaxRadiusInMeters;

    this.locationService.updateConfiguration(this.location.id, configuration)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(result => {
        FormsUtils.markFormAsPristine(this.locationDetailForm);
        this.location.configuration = result;
      }, e => this.errorHandler.handleHttpError(e, 'local-loads.locations.detail.errors.update-configuration'))
  }

  updateLocationCoords(latng: Latng) {
    this.location.latitude  = latng.latitude;
    this.location.longitude = latng.longitude;

    this.loader.startLoadAndCloseWhenComplete(
      this.locationService.updateCoords(this.location.id, latng)
    ).subscribe((container) => {
      this.location.coordinatesValidated                   = true;
      this.location.latitude                               = latng.latitude;
      this.location.longitude                              = latng.longitude;
      this.location.configuration.googleTimezone           = container.configuration.googleTimezone;
      this.location.configuration.googleTimezoneResolvedOn = container.configuration.googleTimezoneResolvedOn;
      this.fetchTimezones();
    }, e => this.errorHandler.handleHttpError(e, 'local-loads.locations.detail.errors.update-coords'))
  }

  validateLocationCoords() {
    this.loader.startLoadAndCloseWhenComplete(
      this.locationService.validateCoords(this.location.id)
    ).subscribe(() => {
      this.location.coordinatesValidated = true;
    }, e => this.errorHandler.handleHttpError(e, 'local-loads.locations.detail.errors.validate-coords'))
  }

  openOptionConfigurationEdition(content: TemplateRef<any>, option: LocalLocationOptionConfiguration) {
    this.optionForm = {
      id: option.id,
      buttonCaption: option.buttonCaption,
      apiUsername: option.apiUsername,
      apiPassword: option.apiPassword,
      usedOnMobile: option.usedOnMobile,
      printMode: option.printMode,
      printerEmail: option.printerEmail,
    };

    this.modalService.open({
      title: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.page-title.edition'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.buttons.update'),
        icon: 'fa fa-save',
        disabled: () => !this.optionForm.apiUsername || !this.optionForm.buttonCaption || !this.optionForm.apiUsername || !this.optionForm.printMode || (this.optionForm.printMode === 'EMAIL' && !this.optionForm.printerEmail)
      },
      beforeResolve: () => this.loader.startLoadAndCloseWhenComplete(
        this.locationService.updateOption(this.location.id, this.optionForm)
          .pipe(
            tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.successes.update'))),
            catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.tablet-configuration.option.form.errors.update', false)),
          )
      ),
    }).subscribe(() => {
      this.fetchLocationOptions();
      this.fetchLocationProfiles();
    });
  }

  openOptionConfigurationCreation(content: TemplateRef<any>) {
    this.optionForm = new LocalLocationOptionConfiguration();
    this.optionForm.usedOnMobile = false;

    this.modalService.open({
      title: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.page-title.creation'),
      content,
      disableModalCloseOutsideClick: true,
      validateButton: {
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.buttons.create'),
        icon: 'fa fa-save',
        disabled: () => !this.optionForm.apiUsername || !this.optionForm.buttonCaption || !this.optionForm.apiUsername || !this.optionForm.printMode || (this.optionForm.printMode === 'EMAIL' && !this.optionForm.printerEmail)
      },
      beforeResolve: () => this.loader.startLoadAndCloseWhenComplete(
        this.locationService.createOption(this.location.id, this.optionForm)
          .pipe(
            tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.successes.create'))),
            catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.tablet-configuration.option.form.errors.create', false)),
          )
      ),
    }).subscribe(() => {
      this.fetchLocationOptions();
    });
  }

  openOptionConfigurationDeletion(content: TemplateRef<any>) {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;

    this.modalService.open({
      title: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.modals.delete.title'),
      content,
      closeButton: {
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.modals.delete.buttons.no'),
      },
      validateButton: {
        type: 'danger',
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.modals.delete.buttons.yes'),
        icon: 'fa fa-trash-alt'
      },
      beforeResolve: () => this.loader.startLoadAndCloseWhenComplete(
        this.locationService.deleteOption(this.optionForm.id)
          .pipe(
            tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.tablet-configuration.option.form.successes.delete'))),
            catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.tablet-configuration.option.form.errors.delete', false)),
          )
      ),
    }).subscribe(() => {
      this.fetchLocationOptions();
      this.fetchLocationProfiles();
    });
  }

  openProfileConfigurationEdition(content: TemplateRef<any>, profile: LocalLocationProfileConfiguration) {
    this.profileForm = {
      id: profile.id,
      name: profile.name,
      checkinAllowed: profile.checkinAllowed,
      checkoutAllowed: profile.checkoutAllowed,
      supportPhoneNumber: profile.supportPhoneNumber,
      options: profile.options
    };

    this.modalService.open({
      title: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.page-title.edition'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.buttons.update'),
        icon: 'fa fa-save',
        disabled: () => !this.profileForm.name || (!this.profileForm.checkinAllowed && !this.profileForm.checkoutAllowed) || (!this.profileForm.supportPhoneNumber) || (!this.phoneInput.valid) || !(this.profileForm.options && this.profileForm.options.length > 0)
      },
      beforeResolve: () => this.loader.startLoadAndCloseWhenComplete(
        this.locationService.updateProfile(this.location.id, this.profileForm)
          .pipe(
            tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.successes.update'))),
            catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.tablet-configuration.profile.form.errors.update', false)),
          )
      ),
    }).subscribe(() => {
      this.fetchLocationProfiles();
    });
  }

  openProfileConfigurationCreation(content: TemplateRef<any>) {
    this.profileForm = new LocalLocationProfileConfiguration();

    this.modalService.open({
      title: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.page-title.creation'),
      content,
      disableModalCloseOutsideClick: true,
      validateButton: {
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.buttons.create'),
        icon: 'fa fa-save',
        disabled: () => !this.profileForm.name || (!this.profileForm.checkinAllowed && !this.profileForm.checkoutAllowed) || (!this.profileForm.supportPhoneNumber) || (!this.phoneInput.valid) || !(this.profileForm.options && this.profileForm.options.length > 0)
      },
      beforeResolve: () => this.loader.startLoadAndCloseWhenComplete(
        this.locationService.createProfile(this.location.id, this.profileForm)
          .pipe(
            tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.successes.create'))),
            catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.tablet-configuration.profile.form.errors.create', false)),
          )
      ),
    }).subscribe(() => {
      this.fetchLocationProfiles();
    });
  }

  openProfileConfigurationDeletion(content: TemplateRef<any>) {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;

    this.modalService.open({
      title: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.modals.delete.title'),
      content,
      closeButton: {
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.modals.delete.buttons.no'),
      },
      validateButton: {
        type: 'danger',
        text: this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.modals.delete.buttons.yes'),
        icon: 'fa fa-trash-alt'
      },
      beforeResolve: () => this.loader.startLoadAndCloseWhenComplete(
        this.locationService.deleteProfile(this.profileForm.id)
          .pipe(
            tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.tablet-configuration.profile.form.successes.delete'))),
            catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.tablet-configuration.profile.form.errors.delete', false)),
          )
      ),
    }).subscribe(() => {
      this.fetchLocationOptions();
      this.fetchLocationProfiles();
    });
  }

  displayIndexOptions(profile: LocalLocationProfileConfiguration): string {
    const profileOptionsId = profile.options.map(option => option.id);
    const optionsId = this.options.map(option => option.id);

    const displayedString = profileOptionsId.map(optionId => optionsId.findIndex((id) => id === optionId) + 1)
      .sort()
      .join(' - ');

    return (displayedString) ? displayedString : this.translate.instant('local-loads.locations.detail.tablet-configuration.option.empty-option');

  }

  etaApproximationSourceChange(onChangeRegion: boolean = false) {
    if  (this.etaApproximationSource === EtaApproximationSource.REGION) {
      if (!onChangeRegion) {
        this.currentBufferDurations = this.configurationForm.bufferDurations;
      }
      this.configurationForm.bufferDurations = this.configurationForm.region.bufferDurations;
    } else if (this.etaApproximationSource === EtaApproximationSource.PERSONALIZED) {
      this.configurationForm.bufferDurations = this.currentBufferDurations;
    }
  }

  regionChange() {
    this.etaApproximationSourceChange(true);
  }

  invalidateCache() {
    this.locationService.invalidateCacheLocalLocation(this.location.id)
      .subscribe(() => {
          this.toastr.success(this.translate.instant('local-loads.locations.detail.information.cache-invalidation-success'));
          this.location.vatPhoneUpdatedAt = null;
        }, e => this.errorHandler.handleHttpError(e, 'local-loads.locations.detail.information.cache-invalidation-unsuccessful'));
  }

  syncWithSAP() {
    this.loader.startLoad();
    this.locationService.syncWithSap(this.location.glid)
      .pipe(
        mergeMap(() => this.locationService.getLocation(this.location.id)),
        finalize(() => this.loader.stopLoad()),
        tap(() => this.toastr.success(this.translate.instant('local-loads.locations.detail.successes.sync-with-sap', { glid: this.location.glid }))),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.locations.detail.errors.sync-with-sap', false)),
      ).subscribe(l => {
        this.location = l;
      });
  }

  getConvertedMetersRadius(newVal: number) {
    return parseFloat((newVal * this.CONVERSION_CONSTANT).toFixed(2));
  }

  getConvertedMilesRadius(newVal: number) {
    return parseFloat((newVal / this.CONVERSION_CONSTANT).toFixed(2));
  }

  downloadForceCheckinPdf() {
    this.loader.startLoad();

    return this.localLocationService.getForceCheckinQrcodePdf(this.location.glid)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe();
  }

  getTranslatedLabel(status: string) {
    const valueWithTranslatedLabel = this.trailerStatuses.find(t => t.value === status);
    return valueWithTranslatedLabel ? valueWithTranslatedLabel.translatedLabel : '';
  }

  launchEditCarrier() {
    this.locationCarrierConfigEditOpen = true;
  }

  closeEditCarrier() {
    this.fetchCarrierLinkedCount();
    this.locationCarrierConfigEditOpen = false;
  }

  private fetchCarrierLinkedCount() {
    this.localLocationService.getCarriersLinkedCount(this.location.id).subscribe(n => this.cmaLiteCarrierLinkedNumber = n);
  }

  openConfiguration() {
    this.activeTab = 'cma-cico-config';
    this.onConfig = true
    this.openConfigureLocation();
  }

  closeConfiguration() {
    if (!this.onConfig) {
      this.onClose.emit();
    }
    this.activeTab = 'info';
    this.onConfig = false;
  }

  public onNumberInputKeydown(event: KeyboardEvent): void {
    const forbiddenKeys = ['e', '-'];
    if (forbiddenKeys.includes(event.key)) {
      event.preventDefault();
    }
  }
}
